import React from 'react';
import { Box, Typography, List, ListItemButton, ListItemIcon, ListItemText, Divider, Chip, IconButton, Tooltip } from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import SettingsIcon from '@mui/icons-material/Settings';
import DownloadIcon from '@mui/icons-material/Download';
import HomeIcon from '@mui/icons-material/Home';
import CampaignIcon from '@mui/icons-material/Campaign';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import CircleIcon from '@mui/icons-material/Circle';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import MenuIcon from '@mui/icons-material/Menu';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { BLOCKED_STATUS_LABEL, BLOCKED_STATUS_BODY } from '../utils/blockedStatus';

const statusLabels = {
  pending: { label: 'Pending', color: '#f59e0b' },
  payment_pending: { label: 'Payment Needed', color: '#f59e0b' },
  onboarding: { label: 'Setup', color: '#3b82f6' },
  details_submitted: { label: 'Under Review', color: '#8b5cf6' },
  otp_ready: { label: 'Verify Number', color: '#3b82f6' },
  otp_verified: { label: 'Verifying', color: '#3b82f6' },
  active: { label: 'Active', color: '#22c55e' },
  suspended: { label: 'Suspended', color: '#ef4444' },
};

const PRIMARY = '#075e54';
const PRIMARY_LIGHT = 'rgba(7,94,84,0.12)';
const COLLAPSED_W = 72;
const EXPANDED_W = 240;

const ClientSidebar = ({ collapsed, onToggle, activeView, onNavigate, status, apiStatus, apiStatusDetail }) => {
  const items = [
    { key: 'dashboard', label: 'Dashboard', icon: <HomeIcon /> },
    ...(status === 'active' || status === 'otp_verified'
      ? [
          { key: 'contacts', label: 'Contacts', icon: <PeopleAltIcon /> },
          { key: 'chats', label: 'Chats', icon: <ChatIcon /> },
          { key: 'chatbotflow', label: 'Chatbot Flow', icon: <SmartToyIcon /> },
          { key: 'campaigns', label: 'Campaigns', icon: <CampaignIcon /> },
          { key: 'settings', label: 'Settings', icon: <SettingsIcon /> },
          { key: 'export', label: 'Export Data', icon: <DownloadIcon /> },
        ]
      : []),
  ];

  const st = apiStatus === 'blocked'
    ? { label: BLOCKED_STATUS_LABEL, color: '#ef4444' }
    : statusLabels[status] || { label: status || 'Unknown', color: '#64748b' };

  return (
    <Box
      sx={{
        width: collapsed ? COLLAPSED_W : EXPANDED_W,
        height: '100vh',
        background: 'linear-gradient(180deg, #075e54 0%, #054d44 60%, #043d36 100%)',
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.3s ease',
        overflow: 'hidden',
        position: 'fixed',
        zIndex: 1200,
      }}
    >
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'space-between',
        py: 1.5, px: collapsed ? 0 : 1.5, minHeight: 56, gap: 1,
        borderBottom: '1px solid rgba(255,255,255,0.08)',
      }}>
        {!collapsed && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <Box component="img" src="/talktokam-green-wa.png" alt="Talktokam"
              sx={{ height: 34, width: 'auto', borderRadius: '6px' }} />
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 15, lineHeight: 1.2 }}>WhatsApp API</Typography>
            </Box>
          </Box>
        )}
        {collapsed && (
          <Box component="img" src="/talktokam-green-wa.png" alt="Talktokam"
            sx={{ height: 30, width: 'auto', borderRadius: '6px' }} />
        )}
        <IconButton onClick={onToggle} size="small"
          sx={{ color: 'rgba(255,255,255,0.7)', p: 0.8, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
          {collapsed ? <MenuIcon fontSize="small" /> : <MenuOpenIcon fontSize="small" />}
        </IconButton>
      </Box>

      {!collapsed && (
        <Box sx={{ px: 2, py: 1.5, textAlign: 'center' }}>
          <Tooltip title={apiStatus === 'blocked' ? BLOCKED_STATUS_BODY : ''} arrow placement="right"
            slotProps={{ popper: { sx: { '& .MuiTooltip-tooltip': { bgcolor: '#1f2937', fontSize: 12, fontWeight: 600, px: 1.5, py: 0.8, borderRadius: 1.5, maxWidth: 260 } } } }}>
            <Chip label={st.label} size="small"
              sx={{ bgcolor: st.color, color: '#fff', fontWeight: 700, fontSize: 11, borderRadius: '8px', cursor: apiStatus === 'blocked' ? 'help' : 'default' }} />
          </Tooltip>
        </Box>
      )}

      <List sx={{ flex: 1, px: collapsed ? 0.5 : 1.5, py: 1 }}>
        {items.map((item) => {
          const sel = activeView === item.key;
          return collapsed ? (
            <Tooltip key={item.key} title={item.label} placement="right" arrow
              slotProps={{ popper: { sx: { '& .MuiTooltip-tooltip': { bgcolor: '#1f2937', fontSize: 12, fontWeight: 600, px: 1.5, py: 0.8, borderRadius: 1.5 } } } }}
            >
              <ListItemButton
                selected={sel}
                onClick={() => onNavigate(item.key)}
                sx={{
                  borderRadius: 2, mb: 0.3, px: 1, py: 0.8, justifyContent: 'center',
                  '&.Mui-selected': { background: 'rgba(255,255,255,0.15)' },
                  '&.Mui-selected:hover': { background: 'rgba(255,255,255,0.2)' },
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 0, color: '#fff', justifyContent: 'center' }}>
                  <Box sx={{
                    width: 32, height: 32, borderRadius: '9px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: sel ? 'rgba(255,255,255,0.12)' : 'transparent',
                  }}>
                    {item.icon}
                  </Box>
                </ListItemIcon>
              </ListItemButton>
            </Tooltip>
          ) : (
            <ListItemButton
              key={item.key}
              selected={sel}
              onClick={() => onNavigate(item.key)}
              sx={{
                borderRadius: 2, mb: 0.3, px: 1.5, py: 1,
                '&.Mui-selected': {
                  background: 'rgba(255,255,255,0.15)',
                  borderLeft: '3px solid #25d366',
                },
                '&.Mui-selected:hover': { background: 'rgba(255,255,255,0.2)' },
                '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
              }}
            >
              <ListItemIcon sx={{ color: '#fff', minWidth: 36, opacity: sel ? 1 : 0.8 }}>
                <Box sx={{
                  width: 30, height: 30, borderRadius: '9px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: sel ? 'rgba(255,255,255,0.12)' : 'transparent',
                }}>
                  {item.icon}
                </Box>
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontSize: 14, fontWeight: sel ? 700 : 500,
                  color: '#fff',
                }}
              />
            </ListItemButton>
          );
        })}
      </List>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
      <Box sx={{
        p: collapsed ? 1 : 2,
        display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'center', gap: 1,
      }}>
        <CircleIcon sx={{ fontSize: 8, color: '#22c55e' }} />
        {!collapsed && (
          <Typography variant="caption" sx={{ opacity: 0.6, fontSize: 11 }}>talktokam.com</Typography>
        )}
      </Box>
    </Box>
  );
};

export default ClientSidebar;
